/**
 * LicenseTimeline — submission history + status update for a license.
 * Shows a chronological timeline of events (submitted, acknowledged, approved, etc.)
 * with a form to add new events.
 */
import { useState, useEffect } from "react";
import { Plus, Clock, Check, X, AlertTriangle, Send, FileText, ChevronDown } from "lucide-react";
import { api } from "../../lib/api.ts";

interface LicenseTimelineProps {
  projectId: string;
  licenseTypeId: string;
  currentStatus: string;
  onStatusChange: () => void;
  autoOpenForm?: boolean;
}

interface Submission {
  id: string;
  event_type: string;
  event_date: string;
  reference_number: string | null;
  regulator_contact: string | null;
  channel: string | null;
  description: string | null;
  follow_up_date: string | null;
  created_at: string;
}

const EVENT_TYPES = [
  { value: "submitted", label: "Application Submitted", icon: Send },
  { value: "acknowledged", label: "Acknowledged by Regulator", icon: Check },
  { value: "under_review", label: "Under Review", icon: Clock },
  { value: "info_requested", label: "Additional Info Requested", icon: AlertTriangle },
  { value: "info_provided", label: "Additional Info Provided", icon: Send },
  { value: "approved", label: "Approved", icon: Check },
  { value: "rejected", label: "Rejected", icon: X },
  { value: "appealed", label: "Appeal Filed", icon: FileText },
  { value: "renewed", label: "Renewed", icon: Check },
  { value: "note", label: "Note / Update", icon: FileText },
];

const EVENT_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  submitted: { bg: "bg-info-light", text: "text-info", dot: "bg-info" },
  acknowledged: { bg: "bg-info-light", text: "text-info", dot: "bg-info" },
  under_review: { bg: "bg-warning-light", text: "text-warning", dot: "bg-warning" },
  info_requested: { bg: "bg-warning-light", text: "text-warning", dot: "bg-warning" },
  info_provided: { bg: "bg-info-light", text: "text-info", dot: "bg-info" },
  approved: { bg: "bg-brand-light", text: "text-brand", dot: "bg-brand" },
  rejected: { bg: "bg-danger-light", text: "text-danger", dot: "bg-danger" },
  appealed: { bg: "bg-warning-light", text: "text-warning", dot: "bg-warning" },
  renewed: { bg: "bg-brand-light", text: "text-brand", dot: "bg-brand" },
  note: { bg: "bg-surface-secondary", text: "text-text-secondary", dot: "bg-text-tertiary" },
};

const CHANNELS = [
  { value: "online", label: "Online portal" },
  { value: "email", label: "Email" },
  { value: "in_person", label: "In person" },
  { value: "post", label: "Post / courier" },
  { value: "other", label: "Other" },
];

export function LicenseTimeline({ projectId, licenseTypeId, currentStatus, onStatusChange, autoOpenForm }: LicenseTimelineProps) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(!!autoOpenForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [eventType, setEventType] = useState("submitted");
  const [eventDate, setEventDate] = useState(new Date().toISOString().split("T")[0]!);
  const [refNumber, setRefNumber] = useState("");
  const [channel, setChannel] = useState("");
  const [description, setDescription] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [regulatorContact, setRegulatorContact] = useState("");

  const load = () => {
    api.getLicenseSubmissions(projectId, licenseTypeId)
      .then(setSubmissions)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [projectId, licenseTypeId]);

  // If parent requests form auto-open (e.g., from "Record submission" button)
  useEffect(() => { if (autoOpenForm) setShowForm(true); }, [autoOpenForm]);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.addLicenseSubmission(projectId, licenseTypeId, {
        event_type: eventType,
        event_date: eventDate,
        reference_number: refNumber || undefined,
        regulator_contact: regulatorContact || undefined,
        channel: channel || undefined,
        description: description || undefined,
        follow_up_date: followUpDate || undefined,
      });
      // Reset form
      setShowForm(false);
      setRefNumber("");
      setChannel("");
      setDescription("");
      setFollowUpDate("");
      setRegulatorContact("");
      load();
      onStatusChange(); // refresh parent license data
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Check for upcoming follow-ups
  const today = new Date().toISOString().split("T")[0]!;
  const pendingFollowUps = submissions.filter(s => s.follow_up_date && s.follow_up_date >= today);

  return (
    <div>
      {/* Follow-up alerts */}
      {pendingFollowUps.length > 0 && (
        <div className="mb-3">
          {pendingFollowUps.map(s => (
            <div key={s.id} className="flex items-center gap-2 px-3 py-2 bg-warning-light rounded-lg text-[13px] text-warning mb-1">
              <Clock size={14} />
              Follow up by {new Date(s.follow_up_date!).toLocaleDateString()}: {s.description || EVENT_TYPES.find(e => e.value === s.event_type)?.label}
            </div>
          ))}
        </div>
      )}

      {/* Add event button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-light text-brand rounded-lg text-[13px] font-medium hover:bg-brand/10 mb-3"
        >
          <Plus size={12} /> Record event
        </button>
      )}

      {/* Add event form */}
      {showForm && (
        <div className="bg-surface-secondary border border-border rounded-xl p-3 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Event</label>
              <select
                value={eventType}
                onChange={e => setEventType(e.target.value)}
                className="w-full px-2 py-1.5 border border-border rounded-lg text-[14px] bg-surface"
              >
                {EVENT_TYPES.map(et => <option key={et.value} value={et.value}>{et.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Date</label>
              <input
                type="date"
                value={eventDate}
                onChange={e => setEventDate(e.target.value)}
                className="w-full px-2 py-1.5 border border-border rounded-lg text-[14px] bg-surface"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Reference #</label>
              <input
                type="text"
                value={refNumber}
                onChange={e => setRefNumber(e.target.value)}
                placeholder="e.g., EPR/GL/2026/045"
                className="w-full px-2 py-1.5 border border-border rounded-lg text-[14px]"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Channel</label>
              <select
                value={channel}
                onChange={e => setChannel(e.target.value)}
                className="w-full px-2 py-1.5 border border-border rounded-lg text-[14px] bg-surface"
              >
                <option value="">—</option>
                {CHANNELS.map(ch => <option key={ch.value} value={ch.value}>{ch.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Details about this event..."
              rows={2}
              className="w-full px-2 py-1.5 border border-border rounded-lg text-[14px] resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Follow up by</label>
              <input
                type="date"
                value={followUpDate}
                onChange={e => setFollowUpDate(e.target.value)}
                className="w-full px-2 py-1.5 border border-border rounded-lg text-[14px]"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Regulator contact</label>
              <input
                type="text"
                value={regulatorContact}
                onChange={e => setRegulatorContact(e.target.value)}
                placeholder="Name or email"
                className="w-full px-2 py-1.5 border border-border rounded-lg text-[14px]"
              />
            </div>
          </div>

          {error && <div className="text-[13px] text-danger">{error}</div>}

          <div className="flex items-center justify-between pt-1">
            <button onClick={() => setShowForm(false)} className="text-[13px] text-text-secondary">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={saving || !eventDate}
              className="px-3 py-1.5 bg-brand text-white rounded-lg text-[13px] font-medium disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save event"}
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-12 bg-surface-tertiary rounded-lg animate-pulse" />)}
        </div>
      ) : submissions.length === 0 ? (
        <p className="text-[13px] text-text-tertiary py-2">No events recorded yet. Click "Record event" to start tracking your licensing journey.</p>
      ) : (
        <div className="relative pl-6">
          {/* Vertical line */}
          <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-border" />

          <div className="space-y-3">
            {submissions.map((s) => {
              const colors = EVENT_COLORS[s.event_type] || EVENT_COLORS.note;
              const eventLabel = EVENT_TYPES.find(e => e.value === s.event_type)?.label || s.event_type;

              return (
                <div key={s.id} className="relative">
                  {/* Timeline dot */}
                  <div className={`absolute -left-6 top-2 w-3.5 h-3.5 rounded-full border-2 border-white ${colors.dot}`} />

                  <div className={`${colors.bg} rounded-lg px-3 py-2`}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={`text-[13px] font-medium ${colors.text}`}>{eventLabel}</span>
                      <span className="text-[12px] text-text-tertiary">{new Date(s.event_date).toLocaleDateString()}</span>
                    </div>
                    {s.reference_number && (
                      <div className="text-[12px] text-text-secondary">Ref: <span className="font-mono">{s.reference_number}</span></div>
                    )}
                    {s.description && (
                      <div className="text-[13px] text-text-secondary mt-1">{s.description}</div>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-text-tertiary">
                      {s.channel && <span>{CHANNELS.find(c => c.value === s.channel)?.label || s.channel}</span>}
                      {s.regulator_contact && <span>Contact: {s.regulator_contact}</span>}
                      {s.follow_up_date && <span className="text-warning">Follow up: {new Date(s.follow_up_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
